import React, { useEffect, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { changeLanguage } from "@/services/slices/setting.slice";
import {
  Building2,
  GraduationCap,
  Home,
  Info,
  Languages,
  LogIn,
  Mail,
  Menu,
  Sparkles,
  UserPlus,
  X,
  type LucideIcon,
} from "lucide-react";
import AppLogo from "@/components/AppLogo";

const AppleIcon: React.FC<{ size?: number; className?: string }> = ({
  size = 18,
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.702z" />
  </svg>
);

const AndroidIcon: React.FC<{ size?: number; className?: string }> = ({
  size = 18,
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9993s.4482-.9993.9993-.9993c.5511 0 .9993.4486.9993.9993s-.4482.9993-.9993.9993zm-11.046 0c-.5511 0-.9993-.4486-.9993-.9993s.4482-.9993.9993-.9993c.5511 0 .9993.4486.9993.9993s-.4482.9993-.9993.9993zm11.4045-6.02l1.9973-3.4592a.416.416 0 0 0-.1521-.5676.416.416 0 0 0-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.0989L4.841 5.4467a.4161.4161 0 0 0-.5676-.1521.4157.4157 0 0 0-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3435-4.1021-2.6892-7.5743-6.1185-9.4396z" />
  </svg>
);

const NAV_LINKS: { to: string; label: string; icon: LucideIcon }[] = [
  { to: "/", label: "Home", icon: Home },
  { to: "/features", label: "Features", icon: Sparkles },
  { to: "/universities", label: "Universities", icon: GraduationCap },
  { to: "/about", label: "About", icon: Info },
  { to: "/contact", label: "Contact", icon: Mail },
];

const APP_STORE_URL = "https://apps.apple.com/us/app/uninfo-hk/id1643965098";
const GOOGLE_PLAY_URL = "https://play.google.com/store/search?q=UninfoHK&c=apps";

const APP_STORES = [
  { href: APP_STORE_URL, label: "Download on the App Store", icon: AppleIcon },
  { href: GOOGLE_PLAY_URL, label: "Get it on Google Play", icon: AndroidIcon },
];

const AppStoreLinks: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      {APP_STORES.map((store) => {
        const Icon = store.icon;
        return (
          <a
            key={store.label}
            href={store.href}
            className="flex items-center gap-2.5 rounded-lg border border-gray-100 px-3 py-2 transition hover:border-[#ffa217] hover:bg-[#fff3e0]"
          >
            <Icon size={18} className="shrink-0 text-[#ffa217]" />
            <span className="text-sm font-medium text-gray-600">
              {t(store.label)}
            </span>
          </a>
        );
      })}
    </div>
  );
};

const SocialIcon: React.FC<{
  label: string;
  href?: string;
  children: React.ReactNode;
}> = ({ label, href = "#", children }) => (
  <a
    href={href}
    aria-label={label}
    target="_blank"
    rel="noopener noreferrer"
    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-[#ffa217]"
  >
    {children}
  </a>
);

/**
 * Public marketing site shell: sticky navbar + footer around an `<Outlet />`.
 * Used by `/`, `/features`, `/universities`, `/about`, `/contact`,
 * `/for-business`.
 */
const PublicLayout: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const language = useSelector((state: any) => state.setting.language);
  const [menuOpen, setMenuOpen] = useState(false);
  const [appMenuOpen, setAppMenuOpen] = useState(false);

  const toggleLanguage = () => {
    dispatch(
      changeLanguage({ language: language === "zh_hant" ? "en" : "zh_hant" }),
    );
  };

  useEffect(() => {
    if (!appMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAppMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [appMenuOpen]);

  const navigationType = useNavigationType();

  useEffect(() => {
    setMenuOpen(false);
    setAppMenuOpen(false);
    // A POP (back/forward) is handled by ScrollRestoration, which restores the
    // previous entry's offset instead of jumping to the top.
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [pathname, navigationType]);

  // Footer links scroll to the top of their target page even when the
  // target equals the current route (the pathname effect won't re-fire).
  const handleFooterNavClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/90 backdrop-blur">
        <div className="relative mx-auto flex h-16 w-full max-w-7xl items-center gap-3 px-4 sm:px-6">
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Link to="/" className="flex shrink-0 items-center gap-2">
              <AppLogo size={36} />
              <span className="hidden text-xl font-bold text-[#333] sm:inline">
                Uninfo<span className="text-[#ffa217]">HK</span>
              </span>
            </Link>

            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setAppMenuOpen((v) => !v);
                }}
                aria-expanded={appMenuOpen}
                aria-haspopup="true"
                className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] sm:px-3"
              >
                <span className="inline-flex items-center gap-1">
                  <AppleIcon size={15} />
                  <AndroidIcon size={15} className="text-[#ffa217]" />
                </span>
                {t("Get the App")}
              </button>

              {appMenuOpen && (
                <div className="absolute left-0 top-full z-50 mt-2 w-64 rounded-2xl border border-gray-100 bg-white p-3 shadow-xl">
                  <AppStoreLinks />
                </div>
              )}
            </div>
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <nav className="hidden items-center gap-0.5 xl:flex">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `whitespace-nowrap rounded-lg px-2.5 py-2 text-sm font-medium transition ${
                      isActive
                        ? "bg-[#fff3e0] text-[#ffa217]"
                        : "text-gray-600 hover:bg-gray-50 hover:text-[#ffa217]"
                    }`
                  }
                >
                  {t(link.label)}
                </NavLink>
              ))}
            </nav>

            <div className="hidden items-center gap-2 xl:flex">
              <Link
                to="/login"
                className="whitespace-nowrap rounded-lg border border-[#ffa217] px-3.5 py-1.5 text-sm font-semibold text-[#ffa217] transition hover:bg-[#fff3e0]"
              >
                {t("Log In")}
              </Link>
              <div className="flex items-center overflow-hidden rounded-lg border border-[#ffa217]">
                <Link
                  to="/signup"
                  className="whitespace-nowrap bg-[#ffa217] px-3.5 py-1.5 text-sm font-semibold text-white transition hover:bg-[#e08f0e]"
                >
                  {t("Join as Student")}
                </Link>
                <Link
                  to="/for-business"
                  className="whitespace-nowrap border-l border-[#ffa217] bg-white px-3.5 py-1.5 text-sm font-semibold text-[#ffa217] transition hover:bg-[#fff3e0]"
                >
                  {t("Join as Business")}
                </Link>
              </div>
              <button
                type="button"
                onClick={toggleLanguage}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
              >
                {language === "zh_hant" ? "EN" : "繁"}
              </button>
            </div>

            <button
              type="button"
              className="shrink-0 rounded-lg p-2 text-gray-600 transition hover:bg-gray-50 hover:text-[#ffa217] xl:hidden"
              onClick={() => {
                setAppMenuOpen(false);
                setMenuOpen((v) => !v);
              }}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            role="menu"
            aria-label={t("Menu")}
            className="absolute right-3 top-full z-50 w-60 rounded-b-2xl border border-t-0 border-gray-100 bg-white py-2 shadow-xl xl:hidden"
          >
            {NAV_LINKS.map((link) => {
              const Icon = link.icon;
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition ${
                      isActive
                        ? "bg-[#fff3e0] font-semibold text-[#ffa217]"
                        : "text-gray-600 hover:bg-gray-50"
                    }`
                  }
                >
                  <Icon size={17} className="shrink-0 text-[#ffa217]" />
                  {t(link.label)}
                </NavLink>
              );
            })}

            <div className="my-2 border-t border-gray-100" />

            <div className="px-4 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t("Get the App")}
            </div>
            <AppStoreLinks />

            <div className="my-2 border-t border-gray-100" />

            <Link
              to="/login"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
            >
              <LogIn size={17} className="shrink-0 text-[#ffa217]" />
              {t("Log In")}
            </Link>
            <Link
              to="/signup"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
            >
              <UserPlus size={17} className="shrink-0 text-[#ffa217]" />
              {t("Join as Student")}
            </Link>
            <Link
              to="/for-business"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
            >
              <Building2 size={17} className="shrink-0 text-[#ffa217]" />
              {t("Join as Business")}
            </Link>

            <button
              type="button"
              role="menuitem"
              onClick={toggleLanguage}
              className="mt-1 flex w-full items-center gap-2.5 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
            >
              <Languages size={17} className="shrink-0 text-[#ffa217]" />
              <span className="ml-auto text-sm font-semibold text-[#ffa217]">
                {language === "zh_hant" ? "EN" : "繁"}
              </span>
            </button>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-[#1f2937] text-gray-300">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2">
              <AppLogo size={36} />
              <span className="text-xl font-bold text-white">
                Uninfo<span className="text-[#ffa217]">HK</span>
              </span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-gray-400">
              {t("The all-in-one campus life platform for Hong Kong university students.")}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <SocialIcon
                label="Instagram"
                href="https://www.instagram.com/uninfohk/"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.9 5.9 0 0 0-2.13 1.38A5.9 5.9 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.8.72 1.47 1.38 2.13a5.9 5.9 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.9 5.9 0 0 0 2.13-1.38 5.9 5.9 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 0 0-1.38-2.13A5.9 5.9 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zM12 16a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm7.85-10.4a1.44 1.44 0 1 1-1.44-1.44 1.44 1.44 0 0 1 1.44 1.44z" />
                </svg>
              </SocialIcon>
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-wider text-white">
              {t("Explore")}
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    onClick={handleFooterNavClick}
                    className="py-1 text-gray-400 transition hover:text-[#ffa217] sm:py-0"
                  >
                    {t(link.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-wider text-white">
              {t("Account")}
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link
                  to="/signup"
                  onClick={handleFooterNavClick}
                  className="py-1 text-gray-400 transition hover:text-[#ffa217] sm:py-0"
                >
                  {t("Join as Student")}
                </Link>
              </li>
              <li>
                <Link
                  to="/for-business"
                  onClick={handleFooterNavClick}
                  className="py-1 text-gray-400 transition hover:text-[#ffa217] sm:py-0"
                >
                  {t("Join as Business")}
                </Link>
              </li>
              <li>
                <Link
                  to="/login"
                  onClick={handleFooterNavClick}
                  className="py-1 text-gray-400 transition hover:text-[#ffa217] sm:py-0"
                >
                  {t("Log In")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-sm font-semibold uppercase tracking-wider text-white">
              {t("Contact")}
            </div>
            <ul className="mt-3 space-y-2 text-sm text-gray-400">
              <li>
                <a href="mailto:enquiry@we2tech.pro" className="py-1 transition hover:text-[#ffa217] sm:py-0">
                  enquiry@we2tech.pro
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 px-4 py-5 text-center text-xs text-gray-500">
          © 2026 Uninfo HK. {t("All rights reserved.")}
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;